import { useState, useEffect, useMemo, useRef, useCallback } from 'preact/hooks';
import type { ProjectTag, SearchResult, SessionSummary } from '@voice-assistant/shared';
import { modelBadge, timeAgo } from '../utils/modelHelpers';
import { useAllSessions } from '../state/sessionListStore';
import { useSettingsStore } from '../state/settings.store';

/* ── Types ─────────────────────────────────────────────────────────── */

export type DrawerTab = 'sessions' | 'commands' | 'settings';

interface Props {
  open: boolean;
  tab: DrawerTab;
  onTabChange: (tab: DrawerTab) => void;
  onClose: () => void;
  projectTags: ProjectTag[];
  currentSessionId: string | null;
  lastDeploy?: { completedAt?: string; requestedBy?: string; status?: string } | null;
  onSwitch: (sessionId: string) => void;
  onRename: (sessionId: string, name: string) => void;
  onDelete: (sessionId: string) => void;
  onNewSession: (model?: string) => void;
  onSwitchToMessage?: (sessionId: string, messageId: string) => void;
  commands?: { name: string; description: string }[];
  onRunCommand?: (cmd: string) => void;
  onOpenSettings?: () => void;
  onLogout?: () => void;
  onDoctor?: () => void;
  onRebuild?: () => void;
  sshConnected?: boolean;
  onSshConnect?: () => void;
  onSshDisconnect?: () => void;
  onToggleLanguage?: () => void;
  isAdmin?: boolean;
  memosEnabled?: boolean;
  // settings-zustand-migration Phase 4: autoSpeak / onToggleAutoSpeak /
  // sttLanguage removed from props. The drawer subscribes to the store directly
  // for the values and invokes the store action for the auto-speak toggle.
}

/* ── Search helpers ────────────────────────────────────────────────── */

function highlightMatch(text: string, query: string): any {
  if (!query.trim()) return text;
  const words = query.trim().split(/\s+/).filter(Boolean);
  const pattern = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(pattern);
  return parts.map((part, i) =>
    pattern.test(part) ? <mark class="search-highlight">{part}</mark> : part
  );
}

function truncateAround(text: string, query: string, maxLen = 200): string {
  const lower = text.toLowerCase();
  const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean);
  let bestIdx = 0;
  for (const w of words) {
    const idx = lower.indexOf(w);
    if (idx >= 0) { bestIdx = idx; break; }
  }
  const start = Math.max(0, bestIdx - 60);
  const end = Math.min(text.length, start + maxLen);
  let snippet = text.slice(start, end);
  if (start > 0) snippet = '…' + snippet;
  if (end < text.length) snippet += '…';
  return snippet;
}

/* ── Main Component ─────────────────────────────────────────────────── */

export function UnifiedDrawer({
  open, tab, onTabChange, onClose,
  projectTags, currentSessionId, lastDeploy,
  onSwitch, onRename, onDelete, onNewSession,
  onSwitchToMessage,
  commands, onRunCommand, onOpenSettings, onLogout, onDoctor, onRebuild,
  sshConnected, onSshConnect, onSshDisconnect,
  onToggleLanguage, isAdmin,
  memosEnabled = true,
}: Props) {
  const sessions = useAllSessions();
  // Phase 4: store-sourced settings (replaces 3 deleted props).
  const autoSpeak = useSettingsStore((s) => s.autoPlay ?? true);
  const sttLanguage = useSettingsStore((s) => s.sttLanguage);
  const onToggleAutoSpeak = () => {
    const next = !useSettingsStore.getState().autoPlay;
    void useSettingsStore.getState().patchMemo({ autoPlay: next });
  };
  // Session state
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editName, setEditName] = useState('');
  const [confirmDeleteId, setConfirmDeleteId] = useState<string | null>(null);
  // Help tooltip state
  const [helpSection, setHelpSection] = useState<string | null>(null);

  // Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [searching, setSearching] = useState(false);
  const [searchActive, setSearchActive] = useState(false);
  const searchDebounce = useRef<ReturnType<typeof setTimeout> | null>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const doSearch = useCallback(async (q: string) => {
    if (!q.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    try {
      const { authFetch } = await import('../api/authFetch');
      const res = await authFetch(`/api/sessions/search?q=${encodeURIComponent(q.trim())}&limit=20`);
      if (res.ok) {
        const data = await res.json();
        setSearchResults(data.results || []);
      }
    } catch (err: any) {
      console.error('Search failed:', err);
    } finally {
      setSearching(false);
    }
  }, []);

  const handleSearchInput = useCallback((value: string) => {
    setSearchQuery(value);
    if (searchDebounce.current) clearTimeout(searchDebounce.current);
    if (!value.trim()) {
      setSearchResults([]);
      setSearching(false);
      return;
    }
    setSearching(true);
    searchDebounce.current = setTimeout(() => doSearch(value), 350);
  }, [doSearch]);

  const handleSearchClear = useCallback(() => {
    setSearchQuery('');
    setSearchResults([]);
    setSearchActive(false);
    setSearching(false);
  }, []);

  const handleSearchResultClick = useCallback((result: SearchResult) => {
    if (onSwitchToMessage) {
      onSwitchToMessage(result.sessionId, result.messageId);
    } else {
      onSwitch(result.sessionId);
    }
    handleSearchClear();
    onClose();
  }, [onSwitchToMessage, onSwitch, handleSearchClear, onClose]);

  // Focus search input when activating
  useEffect(() => {
    if (searchActive && searchInputRef.current) {
      searchInputRef.current.focus();
    }
  }, [searchActive]);

  // Clear search when drawer closes
  useEffect(() => {
    if (!open) handleSearchClear();
  }, [open, handleSearchClear]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [open, onClose]);

  const regularSessions = useMemo(() =>
    sessions.filter((s) => s.status !== 'cleared'),
    [sessions],
  );

  if (!open) return null;

  /* ── Session handlers ── */

  const handleStartRename = (s: SessionSummary) => {
    setEditingId(s.id);
    setEditName(s.name || '');
    setConfirmDeleteId(null);
  };

  const handleSaveRename = () => {
    if (editingId && editName.trim()) {
      onRename(editingId, editName.trim());
    }
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    onDelete(id);
    setConfirmDeleteId(null);
  };

  const handleSwitch = (id: string) => {
    if (id !== currentSessionId) onSwitch(id);
    onClose();
  };

  return (
    <>
      <div class="drawer-overlay" onClick={() => { setHelpSection(null); onClose(); }} />
      <div class="unified-drawer">
        {/* Tab switcher */}
        <div class="unified-drawer-tabs">
          <button
            class={`unified-drawer-tab${tab === 'sessions' ? ' active' : ''}`}
            onClick={() => onTabChange('sessions')}
          >
            Sessions
          </button>
          <button
            class={`unified-drawer-tab${tab === 'commands' ? ' active' : ''}`}
            onClick={() => onTabChange('commands')}
          >
            Commands
          </button>
        </div>

        {/* ── Sessions Tab ── */}
        {tab === 'sessions' && (
          <>
            {/* Search bar */}
            <div class="drawer-search-bar">
              {searchActive ? (
                <div class="drawer-search-input-row">
                  <input
                    ref={searchInputRef}
                    class="drawer-search-input"
                    type="text"
                    placeholder="Search all sessions..."
                    value={searchQuery}
                    onInput={(e) => handleSearchInput((e.target as HTMLInputElement).value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Escape') handleSearchClear();
                    }}
                  />
                  {searching && <span class="drawer-search-spinner" />}
                  <button class="drawer-search-close" onClick={handleSearchClear} title="Close search">&times;</button>
                </div>
              ) : (
                <button
                  class="drawer-search-toggle"
                  onClick={() => setSearchActive(true)}
                  title="Search across all sessions"
                >
                  <span class="drawer-search-icon">&#128269;</span>
                  <span>Search sessions...</span>
                </button>
              )}
            </div>

            {/* Search results overlay */}
            {searchActive && searchQuery.trim() ? (
              <div class="unified-drawer-list">
                {searching && searchResults.length === 0 && (
                  <div class="drawer-search-status">Searching...</div>
                )}
                {!searching && searchResults.length === 0 && searchQuery.trim() && (
                  <div class="drawer-search-status">No results for "{searchQuery}"</div>
                )}
                {searchResults.map((r) => (
                  <div
                    key={r.messageId}
                    class="search-result-item"
                    onClick={() => handleSearchResultClick(r)}
                  >
                    <div class="search-result-session">
                      {r.sessionName || 'Untitled session'}
                      <span class="search-result-role">{r.role}</span>
                    </div>
                    <div class="search-result-text">
                      {highlightMatch(truncateAround(r.text, searchQuery), searchQuery)}
                    </div>
                    <div class="search-result-meta">
                      <span>{timeAgo(r.createdAt)}</span>
                      <span class="search-result-score">{Math.round(r.score * 100)}% match</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
            <div class="unified-drawer-list">
              {/* Sessions */}
              <div class="drawer-section-header" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <span>Sessions</span>
                <button
                  class="drawer-help-btn"
                  onClick={(e) => { e.stopPropagation(); setHelpSection(helpSection === 'sessions' ? null : 'sessions'); }}
                  title="What are Sessions?"
                >?</button>
              </div>
              {helpSection === 'sessions' && (
                <div class="drawer-help-text">
                  Standalone Claude conversations. Opus is the most capable, Sonnet is fast and affordable, Haiku is ultra-fast and cheapest.
                  <a href="/guide#sessions" onClick={onClose}>Guide &rarr;</a>
                </div>
              )}
              {regularSessions.map((s) => {
                const isCurrent = s.id === currentSessionId;
                const displayName = s.name || s.previewText || 'Untitled session';
                const truncated = displayName.length > 60 ? displayName.slice(0, 60) + '…' : displayName;
                return (
                  <div key={s.id} class={`session-item${isCurrent ? ' current' : ''}`}>
                    {editingId === s.id ? (
                      <div class="session-rename-row">
                        <input
                          class="session-rename-input"
                          value={editName}
                          onInput={(e) => setEditName((e.target as HTMLInputElement).value)}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') handleSaveRename();
                            if (e.key === 'Escape') setEditingId(null);
                          }}
                          autoFocus
                        />
                        <button class="session-action-btn save" onClick={handleSaveRename}>Save</button>
                        <button class="session-action-btn" onClick={() => setEditingId(null)}>Cancel</button>
                      </div>
                    ) : (
                      <>
                        <div class="session-item-main" onClick={() => handleSwitch(s.id)}>
                          <div class="session-item-name">{truncated}</div>
                          <div class="session-item-meta">
                            {(() => { const mb = modelBadge(s.model, s.isRelay); return <span class={`session-model-badge ${mb.cls}`}>{mb.label}</span>; })()}
                            <span class={`status-badge ${s.status}`}>{s.status}</span>
                            {s.numTurns > 0 && <span>{s.numTurns} turns</span>}
                            {s.totalCostUsd != null && s.totalCostUsd > 0 && (
                              <span>${Number(s.totalCostUsd).toFixed(3)}</span>
                            )}
                            <span>{timeAgo(s.updatedAt)}</span>
                          </div>
                        </div>
                        <div class="session-item-actions">
                          <button class="session-action-btn" onClick={() => handleStartRename(s)} title="Rename">Rename</button>
                          {confirmDeleteId === s.id ? (
                            <>
                              <button class="session-action-btn delete" onClick={() => handleDelete(s.id)}>Confirm</button>
                              <button class="session-action-btn" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
                            </>
                          ) : (
                            <button class="session-action-btn delete" onClick={() => setConfirmDeleteId(s.id)} title="Delete">Delete</button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
              {regularSessions.length === 0 && (
                <div style={{ padding: '12px 16px', color: '#6666aa', fontSize: 12 }}>
                  No sessions yet
                </div>
              )}

            </div>
            )}
            {lastDeploy?.completedAt && (
              <div style={{
                padding: '8px 16px',
                borderTop: '1px solid var(--border-medium)',
                fontSize: 11,
                color: 'var(--text-dim)',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}>
                <span>Last deploy: {timeAgo(lastDeploy.completedAt)}</span>
                <span style={{ color: lastDeploy.status === 'success' ? '#4ecca3' : lastDeploy.status === 'failed' ? '#e94560' : 'var(--text-dim)' }}>
                  {lastDeploy.status === 'success' ? 'OK' : lastDeploy.status === 'failed' ? 'FAILED' : lastDeploy.status}
                </span>
              </div>
            )}
          </>
        )}

        {/* ── Commands Tab ── */}
        {tab === 'commands' && (
          <div class="unified-drawer-list">
            <div class="drawer-section-header">Slash Commands</div>
            {commands && commands.length > 0 ? (
              <div class="drawer-commands-grid">
                {commands.map((cmd) => (
                  <button
                    key={cmd.name}
                    class="drawer-command-btn"
                    onClick={() => { onRunCommand?.(`/${cmd.name}`); onClose(); }}
                    title={cmd.description}
                  >
                    <span class="drawer-command-name">/{cmd.name}</span>
                    <span class="drawer-command-desc">{cmd.description}</span>
                  </button>
                ))}
              </div>
            ) : (
              <div style={{ padding: '16px', color: '#6666aa', fontSize: 13, textAlign: 'center' }}>
                Connect to load available commands
              </div>
            )}
            <div class="drawer-section-header" style={{ marginTop: 8 }}>Quick Links</div>
            <div class="drawer-commands-grid">
              <button class="drawer-command-btn" onClick={() => { window.location.href = '/guide'; }}>
                <span class="drawer-command-name">Guide</span>
                <span class="drawer-command-desc">Usage guide and reference</span>
              </button>
              {memosEnabled && (
                <button class="drawer-command-btn" onClick={() => { window.location.href = '/memos'; }}>
                  <span class="drawer-command-name">Board</span>
                  <span class="drawer-command-desc">Capture, plans, roadmaps</span>
                </button>
              )}
              <button class="drawer-command-btn" onClick={() => { window.location.href = '/monitor'; }}>
                <span class="drawer-command-name">Pulse</span>
                <span class="drawer-command-desc">Live stack &amp; service health</span>
              </button>
              {isAdmin && (
                <button class="drawer-command-btn" onClick={() => { window.location.href = '/admin'; }}>
                  <span class="drawer-command-name">Admin</span>
                  <span class="drawer-command-desc">Admin panel</span>
                </button>
              )}
            </div>
          </div>
        )}

      </div>
    </>
  );
}
